// What a product's own pages read and write, as the member themselves through row level security
// (docs/data-model.md 6, 8.2, 10.2 and 10.3). Row level security decides what comes back: content
// only where has_access says so, a recording's content only once it's released to them, a file,
// link, text or video body only once its placement opens, their own progress, ticks and state, the
// WhatsApp link only while their intake runs, and their own certificate. While staff view as a
// member, the reads are that member's and the pages write nothing (lib/viewAs.jsx).
//
// The Library's rows (src/library/load.js) carry fewer columns; a product page reads its own, for
// 1 product, once, and keeps them in ProductData (ProductData.jsx).
import { supabase, must } from '../lib/supabase.js'
import { callHubFunction } from '../lib/functions.js'
import {
  PRODUCT_COLUMNS, SESSION_COLUMNS, CONTENT_FULL,
  PROGRESS_COLUMNS, HOMEWORK_COLUMNS, SECTION_COLUMNS, PLACEMENT_COLUMNS, ITEM_COLUMNS, BODY_COLUMNS, CHECKLIST_COLUMNS,
  STATE_COLUMNS, CERTIFICATE_COLUMNS, COHORT_LINK_COLUMNS, columnsFor,
} from './columns.js'

const q = (table, columns) => supabase.from(table).select(columns)

// Everything 1 product's pages draw from. Ordered by id where the order isn't the page's own, so a
// recorded answer can't drift by row order alone. type and slug are the product's, as the Library's
// own catalogue row has them: a mastermind's read adds each recording's group, its tags and the
// group's WhatsApp link; Profit On Your Terms's adds the weeks' pack lines, Start here's screens and
// answers, and its intakes' deal surgery (columns.js columnsFor).
export async function loadProductData(productId, personId, { type = null, slug = null } = {}) {
  const cols = columnsFor({ type, slug })
  const [
    products, cohorts, modules, sessions, recordings, homework, ticks,
    sections, placements, checklist, checklistTicks, states, extras, certificates, steps, stepProgress, cohortLinks,
  ] = await Promise.all([
    q('catalogue', PRODUCT_COLUMNS).eq('id', productId).then(must),
    q('cohorts', cols.cohorts).eq('product_id', productId).order('id').then(must),
    q('modules', cols.modules).eq('product_id', productId).order('sort_order').order('id').then(must),
    q('sessions', SESSION_COLUMNS).eq('product_id', productId).order('starts_at').order('id').then(must),
    q('recordings', cols.recordings).eq('product_id', productId).order('sort_order').order('id').then(must),
    q('homework_items', HOMEWORK_COLUMNS).eq('product_id', productId).order('sort_order').order('id').then(must),
    q('homework_ticks', 'item_id').eq('person_id', personId).order('item_id').then(must),
    q('resource_sections', SECTION_COLUMNS).eq('product_id', productId).order('sort_order').order('id').then(must),
    q('resource_placements', PLACEMENT_COLUMNS).eq('product_id', productId).order('sort_order').order('id').then(must),
    q('checklist_items', CHECKLIST_COLUMNS).eq('product_id', productId).order('sort_order').order('id').then(must),
    q('checklist_ticks', 'item_id').eq('person_id', personId).order('item_id').then(must),
    q('member_product_state', STATE_COLUMNS).eq('person_id', personId).eq('product_id', productId).then(must),
    q('product_extras', 'product_id, whatsapp_url').eq('product_id', productId).then(must),
    q('certificates', CERTIFICATE_COLUMNS).eq('product_id', productId).order('id').then(must),
    q('steps', cols.steps).eq('product_id', productId).order('sort_order').order('id').then(must),
    q('step_progress', cols.stepProgress).eq('person_id', personId).order('step_id').then(must),
    cols.cohortLinks ? q('cohort_links', COHORT_LINK_COLUMNS).order('cohort_id').then(must) : [],
  ])

  const product = products[0] || null
  const recIds = recordings.map(r => r.id).sort()
  const live = placements.filter(pl => !pl.removed_at)
  const itemIds = [...new Set(live.map(pl => pl.item_id))].sort()
  // then what hangs off those rows: each recording's length and chapters (content comes back only
  // once it's released to them) and their place in it; the items (titles and sublines) and, for the
  // ones that have opened, their bodies; and whether each placement with a release rule has opened,
  // which is the database's own answer (hub.can_open_placement), because a member with no intake
  // can't read the sessions it waits on
  const waiting = live.filter(pl => pl.release_kind !== 'now')
  const [content, progress, items, bodies, opens] = await Promise.all([
    recIds.length ? q('recording_content', cols.contentLight).in('recording_id', recIds).order('recording_id').then(must) : [],
    recIds.length ? q('recording_progress', PROGRESS_COLUMNS).eq('person_id', personId).in('recording_id', recIds).order('recording_id').then(must) : [],
    itemIds.length ? q('resource_items', ITEM_COLUMNS).in('id', itemIds).order('id').then(must) : [],
    itemIds.length ? q('resource_item_bodies', BODY_COLUMNS).in('item_id', itemIds).order('item_id').then(must) : [],
    Promise.all(waiting.map(pl => supabase.rpc('can_open_placement', { p_placement: pl.id }).then(must)
      .then(open => [pl.id, open === true]))),
  ])
  const open = Object.fromEntries(live.map(pl => [pl.id, pl.release_kind === 'now']))
  for (const [id, yes] of opens) open[id] = yes
  // a mastermind's group link: their own open group's (the rows that come back are only theirs)
  const groupIds = new Set(cohorts.map(c => c.id))
  const groupLink = cohortLinks.find(l => groupIds.has(l.cohort_id) && /^https:\/\//.test(l.whatsapp_invite_url || '')) || null

  return {
    product,
    cohorts,
    modules,
    sessions,
    recordings,
    content,
    progress,
    homework: homework.filter(h => !h.removed_at),
    ticks: ticks.map(t => t.item_id),
    sections,
    placements: live,
    items,
    bodies,
    open,
    checklist,
    checklistTicks: checklistTicks.map(t => t.item_id),
    state: states[0] || null,
    // the WhatsApp link the checklist and the home open: the intake's while it runs (product_extras),
    // or a mastermind's group's own (cohort_links)
    whatsappUrl: extras[0]?.whatsapp_url || groupLink?.whatsapp_invite_url || null,
    cohortLinks,
    certificate: certificates[0] || null,
    steps: steps.filter(s => s.is_published),
    stepProgress,
  }
}

// A recording's whole content: the player's link, chapters, summary, the 5 things and the key
// learnings. Nothing comes back until it's released to them.
export async function loadRecordingContent(recordingId) {
  const rows = await q('recording_content', CONTENT_FULL).eq('recording_id', recordingId).then(must)
  return rows[0] || null
}

// The published layout of 1 page (hub.my_layout), or null: the page then uses its default in code
export async function loadLayout(productId, page) {
  const rows = await supabase.rpc('my_layout', { p_product: productId, p_page: page }).then(must)
  return rows?.[0] || null
}

// ---- Writes. Each is the member's own row; while staff view as a member none of them is called.

// The Library's "used last first": when a product's page opens
export async function markOpened(personId, productId) {
  return must(await supabase.from('member_product_state')
    .upsert({ person_id: personId, product_id: productId, last_opened_at: new Date().toISOString() }, { onConflict: 'person_id,product_id' }))
}

// Where they are in a recording. The database keeps furthest and watched only growing, caps every
// number at the recording's length and sets watched_at itself (merge_recording_progress).
export async function saveProgress(personId, recordingId, { position, furthest, watched }) {
  const n = v => Math.max(0, Math.floor(Number(v) || 0))
  const rows = await supabase.from('recording_progress').upsert({
    person_id: personId, recording_id: recordingId,
    position_seconds: n(position), furthest_seconds: n(furthest), seconds_watched: n(watched),
  }, { onConflict: 'person_id,recording_id' }).select(PROGRESS_COLUMNS).then(must)
  return rows?.[0] || null
}

export async function tickHomework(personId, itemId, done) {
  if (done) return must(await supabase.from('homework_ticks').insert({ person_id: personId, item_id: itemId }))
  return must(await supabase.from('homework_ticks').delete().eq('person_id', personId).eq('item_id', itemId))
}

export async function tickChecklist(personId, itemId, done) {
  if (done) return must(await supabase.from('checklist_ticks').insert({ person_id: personId, item_id: itemId }))
  return must(await supabase.from('checklist_ticks').delete().eq('person_id', personId).eq('item_id', itemId))
}

export async function hideWelcome(personId, productId) {
  return must(await supabase.from('member_product_state')
    .upsert({ person_id: personId, product_id: productId, welcome_hidden_at: new Date().toISOString() }, { onConflict: 'person_id,product_id' }))
}

// Ask Adam about this minute: a question with a link to the minute (a path inside the hub)
export async function askAdam(row) {
  return must(await supabase.from('questions').insert(row))
}

// Opens per resource (decision 11) for text, links and videos; sign-file logs files and tools
export async function logOpen(placementId, action) {
  return must(await supabase.rpc('log_resource_open', { p_placement: placementId, p_action: action }))
}

// A file or tool: a link signed for 2 minutes (sign-file), opened at once and never kept. A refusal
// throws a HubFunctionError carrying the function's own line.
export function signPlacement(placementId) {
  return callHubFunction('sign-file', { placement_id: placementId })
}

// The certificate's PDF (certificate-pdf), as a Blob
export function certificatePdf(certificateId) {
  return callHubFunction('certificate-pdf', { certificate_id: certificateId })
}
